import { call, put, cancelled } from 'redux-saga/effects';

import { intl } from '@helpers/intl';
import { toastError, toastSuccess } from '@store/notifications';

/**
 * @typedef sagaConfig Configuration for saga wrapper
 * @param {Function} requestHandler Saga/function to execute
 * @param {Function} [handleFieldErrors] Handler for field errors
 * @param {boolean} [shouldToastError=true] Show a toast on error
 * @param {boolean} [shouldToastFieldError] Should the first field error be used for a toast
 * @param {Object} [successToast] Message to toast on success. No toast will happen if the message is not defined
 * @param {Object} [fallBackError] Translation object for fallback error message
 * @param {Object} [cancelAction] Action to listen for to cancel the saga
 * @param {Function} [failureAction] Action creator to dispatch on failure. No dispatch will happen when this is not defined
 * @param {Function} [successAction] Action creator to dispatch on success. No dispatch will happen when this is not defined
 * @param {Function} [onFailure] Callback function to run on failure
 * @param {Function} [onSuccess] Callback function to run on success
 * @param {Function} [onCancel] Callback function to saga cancellation
 */

/**
 * General saga wrapper that includes error handling
 * @param {Object} $0 Action
 * @param {Object} $0.payload Action payload
 * @param {sagaConfig} $0.payload.sagaConfig Saga wrapper configuration
 */
export const executeRequest = function* ({ payload }) {
  try {
    const { sagaConfig, ...requestPayload } = payload;
    const { requestHandler, successToast, successAction, onSuccess } = sagaConfig;

    // Handle request
    const result = yield call(requestHandler, { payload: requestPayload });

    // Dispatch success action
    if (successAction) {
      yield put(successAction({ result }));
    }

    // Toast success
    if (successToast) {
      yield put(
        toastSuccess({
          message: successToast,
          values: successToast.values,
        })
      );
    }

    // Execute onSuccess
    if (onSuccess) {
      yield call(onSuccess, result);
    }
  } catch (error) {
    const { nonFieldErrors, hasFieldError, fieldErrors = {} } = error.data || {};
    const { sagaConfig, ...requestPayload } = payload;
    const { handleFieldErrors, shouldToastFieldError, fallBackError, failureAction, onFailure } =
      sagaConfig;
    const shouldToastError = payload?.sagaConfig?.shouldToastError ?? true;

    // Handle field errors
    if (hasFieldError && handleFieldErrors) {
      yield call(handleFieldErrors, fieldErrors);
    }

    // Toast error
    if (shouldToastError) {
      // Extract error message for toast. Non field error, first field error, fallback or default
      const errorToast = nonFieldErrors ||
        (shouldToastFieldError && Object.values(fieldErrors)?.[0]) ||
        fallBackError || {
          defaultMessage: 'An unexpected error occurred, please try again.',
          id: 'global.notification.api-error-default',
        };
      yield put(
        toastError({
          message: errorToast,
        })
      );
    }

    // Dispatch failure action
    if (failureAction) {
      // Extract error message for dispatch. Non field error, first field error, fallback or default
      const errorMessage =
        nonFieldErrors ||
        Object.values(fieldErrors)?.[0] ||
        intl.formatMessage(
          fallBackError || {
            defaultMessage: 'An unexpected error occurred, please try again.',
            id: 'global.notification.api-error-default',
          }
        );

      yield put(failureAction({ errorMessage, requestPayload }));
    }

    // Execute failure callback
    if (onFailure) {
      yield call(onFailure, error, requestPayload);
    }
  } finally {
    const { onCancel } = payload?.sagaConfig || {};
    // Execute cancellation callback
    if (yield cancelled() && onCancel) {
      yield call(onCancel);
    }
  }
};
